<template>
  <div class="politica-privacidad-page bg-light">
    <!-- Header Banner -->
    <header class="privacy-header py-5 text-center text-white">
      <div class="container py-3">
        <h1 class="display-4 font-weight-bold">Política de Privacidad</h1>
        <p class="lead mb-0">Tu privacidad y la seguridad de tus datos contables son nuestra máxima prioridad.</p>
        <div class="header-divider mx-auto mt-3"></div>
      </div>
    </header>

    <!-- Main Content Section -->
    <main class="container py-5">
      <div class="row">
        <!-- Sidebar Navigation (Desktop only) -->
        <aside class="col-lg-3 d-none d-lg-block">
          <div class="sticky-sidebar card border-0 shadow-sm p-3 mb-4">
            <h5 class="card-title font-weight-bold mb-3 text-dark px-2">Secciones</h5>
            <div class="nav flex-column nav-pills privacy-nav">
              <a href="#intro" class="nav-link mb-1 py-2 px-3 rounded align-items-center d-flex text-secondary" :class="{ 'active-tab': currentSectionId === 'intro' }" @click.prevent="scrollTo('#intro')">
                <i class="ri-information-line mr-2"></i> 1. Introducción
              </a>
              <a href="#datos" class="nav-link mb-1 py-2 px-3 rounded align-items-center d-flex text-secondary" :class="{ 'active-tab': currentSectionId === 'datos' }" @click.prevent="scrollTo('#datos')">
                <i class="ri-database-2-line mr-2"></i> 2. Datos que Recopilamos
              </a>
              <a href="#uso" class="nav-link mb-1 py-2 px-3 rounded align-items-center d-flex text-secondary" :class="{ 'active-tab': currentSectionId === 'uso' }" @click.prevent="scrollTo('#uso')">
                <i class="ri-settings-4-line mr-2"></i> 3. Uso de la Información
              </a>
              <a href="#seguridad" class="nav-link mb-1 py-2 px-3 rounded align-items-center d-flex text-secondary" :class="{ 'active-tab': currentSectionId === 'seguridad' }" @click.prevent="scrollTo('#seguridad')">
                <i class="ri-shield-keyhole-line mr-2"></i> 4. Seguridad de los Datos
              </a>
              <a href="#compartir" class="nav-link mb-1 py-2 px-3 rounded align-items-center d-flex text-secondary" :class="{ 'active-tab': currentSectionId === 'compartir' }" @click.prevent="scrollTo('#compartir')">
                <i class="ri-share-forward-line mr-2"></i> 5. Transferencia de Datos
              </a>
              <a href="#derechos" class="nav-link mb-1 py-2 px-3 rounded align-items-center d-flex text-secondary" :class="{ 'active-tab': currentSectionId === 'derechos' }" @click.prevent="scrollTo('#derechos')">
                <i class="ri-user-settings-line mr-2"></i> 6. Tus Derechos (ARCO)
              </a>
              <a href="#cookies" class="nav-link mb-1 py-2 px-3 rounded align-items-center d-flex text-secondary" :class="{ 'active-tab': currentSectionId === 'cookies' }" @click.prevent="scrollTo('#cookies')">
                <i class="ri-fingerprint-line mr-2"></i> 7. Cookies y Rastreo
              </a>
              <a href="#contacto" class="nav-link mb-1 py-2 px-3 rounded align-items-center d-flex text-secondary" :class="{ 'active-tab': currentSectionId === 'contacto' }" @click.prevent="scrollTo('#contacto')">
                <i class="ri-mail-line mr-2"></i> 8. Soporte y Contacto
              </a>
            </div>
            <div class="mt-4 pt-3 border-top text-center">
              <a href="/" class="btn btn-outline-primary btn-sm btn-block">
                <i class="ri-arrow-left-line mr-1"></i> Volver al Inicio
              </a>
            </div>
          </div>
        </aside>

        <!-- Privacy Content -->
        <article class="col-lg-9 col-12">
          <div class="card border-0 shadow-sm p-4 p-md-5 bg-white content-card">
            
            <div class="last-updated mb-4 text-muted small">
              <i class="ri-calendar-line mr-1"></i> Última actualización: Mayo de 2026
            </div>

            <!-- Section 1: Intro -->
            <section id="intro" class="privacy-section mb-5">
              <div class="d-flex align-items-center mb-3">
                <span class="icon-box bg-light-blue text-primary mr-3">
                  <i class="ri-information-line h4 mb-0"></i>
                </span>
                <h3 class="h4 font-weight-bold text-dark m-0">1. Introducción y Responsable del Tratamiento</h3>
              </div>
              <p>
                En <strong>Alliance Inteligencia Contable</strong> (en adelante, "Alliance", "nosotros" o "la Plataforma"), valoramos profundamente la confianza que depositas en nosotros al utilizar nuestro software de gestión contable, facturación e inventario en la nube.
              </p>
              <p>
                Esta Política de Privacidad describe de manera clara, transparente y detallada cómo recopilamos, procesamos, almacenamos, protegemos y gestionamos tu información personal y los datos financieros y comerciales de tu negocio en relación con el uso de nuestros servicios.
              </p>
              <p>
                Al registrarte en la Plataforma, crear una cuenta o interactuar con nuestros sitios web, aceptas libremente las prácticas descritas en este documento de conformidad con las legislaciones locales de protección de datos (incluyendo normativas en Venezuela, Panamá y estándares internacionales como el Reglamento General de Protección de Datos - GDPR).
              </p>
            </section>

            <!-- Section 2: Datos Recopilados -->
            <section id="datos" class="privacy-section mb-5">
              <div class="d-flex align-items-center mb-3">
                <span class="icon-box bg-light-blue text-primary mr-3">
                  <i class="ri-database-2-line h4 mb-0"></i>
                </span>
                <h3 class="h4 font-weight-bold text-dark m-0">2. Datos que Recopilamos</h3>
              </div>
              <p>
                Para proporcionarte nuestros servicios de inteligencia contable con la máxima eficiencia, requerimos tratar los siguientes tipos de datos:
              </p>
              <ul class="custom-list mb-3">
                <li>
                  <strong>Información de Registro de Usuario:</strong> Nombre completo, dirección de correo electrónico, contraseña cifrada, número de teléfono y cargo en la organización.
                </li>
                <li>
                  <strong>Información de la Empresa/Negocio:</strong> Razón social, identificación fiscal (RIF en Venezuela, RUC en Panamá, u homólogos), dirección física, actividad comercial, régimen tributario y moneda de preferencia para la emisión de reportes.
                </li>
                <li>
                  <strong>Datos de Operación del Sistema (Datos del Cliente):</strong> Al utilizar Alliance, procesamos y almacenamos los datos que introduces directamente para llevar a cabo tu contabilidad. Esto incluye registros de ventas, compras, cuentas por cobrar y por pagar, información de facturación (clientes, productos, precios, impuestos), movimientos bancarios y saldos de caja, y control de inventarios (lotes y series).
                </li>
                <li>
                  <strong>Datos de Uso Técnico y Navegación:</strong> Dirección IP, tipo de navegador, sistema operativo, páginas visitadas antes y después de acceder a nuestra web, interacciones dentro de la plataforma y datos de registro del servidor para fines de depuración y seguridad.
                </li>
              </ul>
              <p class="alert-info-box p-3 rounded mb-0">
                <i class="ri-focus-3-line mr-2 text-primary"></i>
                <strong>Aclaratoria sobre Datos del Cliente:</strong> Todo dato contable o comercial registrado en tu espacio de trabajo es de tu absoluta propiedad. Alliance actúa únicamente como <strong>Encargado del Tratamiento</strong> de dichos datos, procesándolos de manera automatizada de acuerdo con tus instrucciones directas para la ejecución del servicio.
              </p>
            </section>

            <!-- Section 3: Uso de la Información -->
            <section id="uso" class="privacy-section mb-5">
              <div class="d-flex align-items-center mb-3">
                <span class="icon-box bg-light-blue text-primary mr-3">
                  <i class="ri-settings-4-line h4 mb-0"></i>
                </span>
                <h3 class="h4 font-weight-bold text-dark m-0">3. Uso de la Información</h3>
              </div>
              <p>
                En Alliance procesamos la información recopilada con las siguientes finalidades legítimas:
              </p>
              <ul class="custom-list">
                <li><strong>Provisión del Servicio:</strong> Habilitar la emisión de facturación digital, procesar transacciones multi-moneda, calcular automáticamente asientos contables en tiempo real y actualizar los inventarios conforme las ventas y compras registradas.</li>
                <li><strong>Soporte Técnico y Atención:</strong> Responder a solicitudes, incidencias, consultas o dudas y guiarte en el uso óptimo de las herramientas de la plataforma.</li>
                <li><strong>Comunicaciones y Alertas:</strong> Enviar notificaciones críticas sobre el sistema (mantenimiento, actualizaciones, expiración de planes, seguridad de la cuenta) o información relevante sobre nuevas funcionalidades, si nos das tu consentimiento previo.</li>
                <li><strong>Garantizar la Seguridad:</strong> Prevenir e identificar accesos no autorizados, fraudes, usos indebidos de la cuenta y asegurar la estabilidad de la infraestructura de TI.</li>
                <li><strong>Cumplimiento Legal:</strong> Cumplir con las obligaciones legales y tributarias aplicables ante los organismos competentes en las jurisdicciones donde operamos.</li>
              </ul>
            </section>

            <!-- Section 4: Seguridad -->
            <section id="seguridad" class="privacy-section mb-5">
              <div class="d-flex align-items-center mb-3">
                <span class="icon-box bg-light-blue text-primary mr-3">
                  <i class="ri-shield-keyhole-line h4 mb-0"></i>
                </span>
                <h3 class="h4 font-weight-bold text-dark m-0">4. Seguridad de los Datos</h3>
              </div>
              <p>
                La protección de tu información financiera y de negocio es un pilar fundamental en Alliance. Implementamos un conjunto de medidas técnicas, organizativas y físicas rigurosas para salvaguardar tu información contra pérdida, robo, alteración, divulgación o acceso no autorizado:
              </p>
              <ul class="custom-list">
                <li><strong>Cifrado Avanzado:</strong> La transmisión de datos entre tu dispositivo y nuestros servidores se realiza de forma segura mediante protocolos de encriptación TLS/SSL (HTTPS) de nivel bancario. Las contraseñas de acceso al sistema se almacenan utilizando algoritmos de hashing irreversible de última generación.</li>
                <li><strong>Infraestructura en la Nube de Alta Seguridad:</strong> Nuestros servicios están alojados en centros de datos con las certificaciones más exigentes del sector de tecnología global, los cuales cuentan con seguridad física perimetral, control de accesos estricto y redundancia de red.</li>
                <li><strong>Copias de Seguridad Automatizadas:</strong> Realizamos respaldos periódicos y cifrados de las bases de datos para garantizar la continuidad del negocio y la recuperación inmediata en caso de imprevistos catastróficos.</li>
                <li><strong>Políticas de Acceso Restringido:</strong> Solo el personal autorizado de soporte o ingeniería tiene acceso restringido a sistemas bajo protocolos de doble factor de autenticación y acuerdos estrictos de confidencialidad, únicamente para fines de solución de problemas previamente autorizados por ti.</li>
              </ul>
            </section>

            <!-- Section 5: Compartir Datos -->
            <section id="compartir" class="privacy-section mb-5">
              <div class="d-flex align-items-center mb-3">
                <span class="icon-box bg-light-blue text-primary mr-3">
                  <i class="ri-share-forward-line h4 mb-0"></i>
                </span>
                <h3 class="h4 font-weight-bold text-dark m-0">5. Transferencia y Divulgación de Datos</h3>
              </div>
              <p>
                <strong>Alliance no comercializa, alquila, vende ni distribuye tus datos a terceros con fines publicitarios o lucrativos.</strong>
              </p>
              <p>
                Solo compartiremos información bajo las siguientes circunstancias excepcionales y estrictamente reguladas:
              </p>
              <ul class="custom-list">
                <li><strong>Proveedores de Servicios Esenciales:</strong> Trabajamos con socios tecnológicos de confianza que proporcionan servicios indispensables para el funcionamiento del sistema (proveedores de infraestructura de hosting en la nube, pasarelas de pago seguro y servicios de correo transaccional). Estos proveedores actúan como subencargados y están contractualmente obligados a utilizar tu información únicamente para los fines específicos encomendados, bajo el mismo nivel de confidencialidad que aplicamos en Alliance.</li>
                <li><strong>Requerimientos Legales y Autoridades:</strong> Podremos revelar información cuando sea legalmente exigido por una autoridad judicial o administrativa competente de conformidad con las leyes vigentes y con el fin de proteger la seguridad jurídica de la Plataforma o de otros usuarios.</li>
              </ul>
            </section>

            <!-- Section 6: Tus Derechos -->
            <section id="derechos" class="privacy-section mb-5">
              <div class="d-flex align-items-center mb-3">
                <span class="icon-box bg-light-blue text-primary mr-3">
                  <i class="ri-user-settings-line h4 mb-0"></i>
                </span>
                <h3 class="h4 font-weight-bold text-dark m-0">6. Tus Derechos como Titular (ARCO)</h3>
              </div>
              <p>
                Garantizamos el ejercicio pleno de tus derechos sobre tu información de carácter personal. En cualquier momento podrás solicitar la ejecución de los siguientes derechos:
              </p>
              <ul class="custom-list mb-4">
                <li><strong>Acceso:</strong> Consultar qué datos personales tenemos sobre ti y cómo están siendo procesados.</li>
                <li><strong>Rectificación:</strong> Solicitar la corrección de datos personales que consideres inexactos, incompletos o desactualizados.</li>
                <li><strong>Cancelación / Eliminación:</strong> Solicitar la supresión de tus datos de nuestros registros cuando consideres que ya no son necesarios para los fines que fueron recopilados, o cuando desees cancelar tu cuenta de manera definitiva.</li>
                <li><strong>Oposición:</strong> Oponerte al tratamiento de tus datos para fines específicos, como el envío de correos promocionales o boletines.</li>
              </ul>
              <p>
                Para ejercer cualquiera de estos derechos, basta con enviar una comunicación escrita desde el correo registrado en tu cuenta a nuestra dirección oficial de soporte: <a :href="`mailto:${email}`" class="font-weight-bold text-primary">{{ email }}</a>, detallando tu requerimiento e incluyendo la identificación de la cuenta asociada.
              </p>
            </section>

            <!-- Section 7: Cookies -->
            <section id="cookies" class="privacy-section mb-5">
              <div class="d-flex align-items-center mb-3">
                <span class="icon-box bg-light-blue text-primary mr-3">
                  <i class="ri-fingerprint-line h4 mb-0"></i>
                </span>
                <h3 class="h4 font-weight-bold text-dark m-0">7. Cookies y Tecnologías de Rastreo</h3>
              </div>
              <p>
                Utilizamos cookies de sesión y almacenamiento local (Local Storage) en el navegador del usuario para asegurar el funcionamiento óptimo de la aplicación:
              </p>
              <ul class="custom-list">
                <li><strong>Cookies Técnicas y de Sesión:</strong> Son indispensables para mantener iniciada tu sesión de forma segura y autorizar tus peticiones a la API mientras navegas por los diferentes módulos contables.</li>
                <li><strong>Cookies de Preferencias:</strong> Almacenan opciones personalizadas del usuario, como la selección del idioma, el país de operación (Venezuela / Panamá) o el modo de visualización de tablas y paneles.</li>
                <li><strong>Cookies Analíticas:</strong> Ocasionalmente empleamos herramientas de análisis de uso anónimo para comprender cómo interactúan los usuarios con la web pública y así mejorar continuamente la experiencia de navegación.</li>
              </ul>
              <p>
                Puedes configurar tu navegador web para rechazar todas las cookies o para indicarte cuándo se envía una cookie. Sin embargo, ten en cuenta que desactivar ciertas cookies impedirá que el sistema y el inicio de sesión funcionen correctamente.
              </p>
            </section>

            <!-- Section 8: Contacto -->
            <section id="contacto" class="privacy-section mb-0">
              <div class="d-flex align-items-center mb-3">
                <span class="icon-box bg-light-blue text-primary mr-3">
                  <i class="ri-mail-line h4 mb-0"></i>
                </span>
                <h3 class="h4 font-weight-bold text-dark m-0">8. Cambios en la Política y Contacto</h3>
              </div>
              <p>
                Nos reservamos el derecho de modificar o actualizar esta Política de Privacidad de manera periódica para adaptarla a mejoras técnicas, cambios regulatorios, de seguridad o nuevas prácticas operativas.
              </p>
              <p>
                Cuando ocurran cambios sustanciales en la forma en que tratamos tus datos personales, te notificaremos de manera visible a través de la plataforma o mediante un correo electrónico informativo antes de que la nueva política entre en vigor. Te recomendamos revisar esta página periódicamente para mantenerte informado.
              </p>
              
              <div class="contact-box p-4 rounded mt-4 bg-light text-center text-md-left border">
                <h5 class="font-weight-bold text-dark mb-3">¿Tienes dudas sobre esta política o la seguridad de tus datos?</h5>
                <p class="text-secondary mb-4">
                  Ponte en contacto directamente con nuestro equipo de Protección de Datos y Privacidad de la Información. Estaremos encantados de ayudarte.
                </p>
                <div class="row">
                  <div class="col-md-6 mb-3 mb-md-0">
                    <div class="d-flex align-items-center justify-content-center justify-content-md-start">
                      <i class="ri-mail-open-line h4 text-primary mr-3 mb-0"></i>
                      <div>
                        <div class="small text-muted font-weight-bold">Correo Electrónico</div>
                        <a :href="`mailto:${email}`" class="text-dark font-weight-bold">{{ email }}</a>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="d-flex align-items-center justify-content-center justify-content-md-start">
                      <i class="ri-whatsapp-line h4 text-success mr-3 mb-0"></i>
                      <div>
                        <div class="small text-muted font-weight-bold">Contacto Directo (WhatsApp)</div>
                        <span @click="enviarMensaje()" class="text-dark font-weight-bold pointer-link">{{ phoneMask }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

          </div>
        </article>
      </div>
    </main>
  </div>
</template>

<script>
import { email, phone, phone_pa } from "../../utils/helpers";

export default {
  name: "PoliticaPrivacidad",
  data() {
    return {
      email: email,
      currentSectionId: 'intro',
      isScrollingProgrammatically: false
    };
  },
  computed: {
    // Detect matching phone number based on active path (VE or PA)
    isPanama() {
      return this.$route.path.toLowerCase().startsWith("/pa");
    },
    phoneMask() {
      return this.isPanama ? phone_pa.mask : phone.mask;
    },
    phoneNumber() {
      return this.isPanama ? phone_pa.number : phone.number;
    }
  },
  mounted() {
    // Ensure the page scrolls to top when loaded
    window.scrollTo({ top: 0 });
    this.setupIntersectionObserver();
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    setupIntersectionObserver() {
      const options = {
        root: null, // use the viewport
        rootMargin: '-120px 0px -60% 0px', // offset for sticky header and trigger when section is in top/middle
        threshold: 0
      };

      this.observer = new IntersectionObserver((entries) => {
        if (this.isScrollingProgrammatically) return;

        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.currentSectionId = entry.target.getAttribute('id');
          }
        });
      }, options);

      const sections = document.querySelectorAll('.privacy-section');
      sections.forEach(section => {
        this.observer.observe(section);
      });
    },
    scrollTo(selector) {
      const element = document.querySelector(selector);
      if (element) {
        this.isScrollingProgrammatically = true;
        this.currentSectionId = selector.replace('#', '');

        element.scrollIntoView({
          behavior: 'smooth'
        });

        // Re-enable observer after the smooth scroll finishes (approx 800ms)
        setTimeout(() => {
          this.isScrollingProgrammatically = false;
        }, 800);
      }
    },
    enviarMensaje() {
      const message = encodeURIComponent("¡Hola! Tengo una consulta sobre la Política de Privacidad de Alliance.");
      const url = `https://wa.me/${this.phoneNumber}?text=${message}`;
      window.open(url, "_blank");
    }
  }
};
</script>

<style lang="scss" scoped>
.politica-privacidad-page {
  font-family: inherit;
  line-height: 1.65;
  color: #495057;
  background-color: #f8f9fa !important;

  .privacy-section {
    scroll-margin-top: 110px; /* Leaves space for the sticky navbar natively */
  }

  .privacy-header {
    background: linear-gradient(135deg, #0c1c2a 0%, #15324d 100%);
    position: relative;
    overflow: hidden;

    .header-divider {
      width: 60px;
      height: 4px;
      background-color: #007bff;
      border-radius: 2px;
    }
  }

  .sticky-sidebar {
    position: sticky;
    top: 110px; /* Space from sticky navbar */
    transition: all 0.3s ease;
  }

  .privacy-nav {
    .nav-link {
      font-size: 0.95rem;
      transition: all 0.2s ease;
      border-left: 3px solid transparent;

      &:hover {
        background-color: rgba(0, 123, 255, 0.05);
        color: #007bff !important;
        border-left-color: rgba(0, 123, 255, 0.3);
      }

      &.active-tab {
        background-color: rgba(0, 123, 255, 0.1) !important;
        color: #007bff !important;
        font-weight: 600;
        border-left-color: #007bff;
      }
    }
  }

  .content-card {
    border-radius: 12px;
    
    p {
      margin-bottom: 1.5rem;
      color: #555;
    }
  }

  .icon-box {
    width: 42px;
    height: 42px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .bg-light-blue {
    background-color: rgba(0, 123, 255, 0.1);
  }

  .custom-list {
    list-style: none;
    padding-left: 0;

    li {
      position: relative;
      padding-left: 1.75rem;
      margin-bottom: 1rem;
      color: #555;

      &::before {
        content: "\eb7b"; /* Remixicon check/bullet check-double or simple icon */
        font-family: "remixicon", sans-serif !important;
        position: absolute;
        left: 0;
        top: 2px;
        color: #007bff;
        font-weight: bold;
        font-size: 1rem;
      }
    }
  }

  .alert-info-box {
    background-color: rgba(23, 162, 184, 0.08);
    border-left: 4px solid #17a2b8;
    color: #17a2b8;
    font-size: 0.95rem;
  }

  .contact-box {
    background-color: #fdfdfd;
    border-color: #e9ecef !important;
  }

  .pointer-link {
    cursor: pointer;
    color: #28a745;
    text-decoration: underline;

    &:hover {
      color: #218838;
    }
  }
}
</style>
